import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { createCheckoutSession, fetchPricing } from '../../services/stripeService';
import './ProModal.css';

const FREE_FEATURES = [
  'Plan & snap routes on the map',
  'Auto-generate routes & loop back home',
  'Curviness & elevation stats',
  'Save routes locally',
  'Browse & load community routes',
  'Publish your routes to the community',
  'GPX export & share links',
];

const PRO_FEATURES = [
  'Everything in Free',
  'Motorcycle-tuned routing engine',
  'Smarter generation — real reachable destinations',
  'Hourly weather along your route',
  'Fuel, viewpoints & food stops on-route',
  'Elevation-baked GPX (Garmin / TomTom-ready)',
];

const PLAN_META = {
  monthly: { label: 'Monthly', suffix: '/mo' },
  yearly:  { label: 'Yearly',  suffix: '/yr', badge: 'Best value' },
};
// Shown until the live Stripe fetch resolves (or if it fails) — today's real
// prices, so this never shows something blank or broken.
const FALLBACK_PRICING = {
  monthly: { amount: 1.99, currency: 'AUD' },
  yearly:  { amount: 9.99, currency: 'AUD' },
};

export function ProModal({ onClose }) {
  const { user, signIn } = useAuth();
  const [plan, setPlan]         = useState('monthly');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [pricing, setPricing]   = useState(FALLBACK_PRICING);
  const overlayRef = useRef(null);

  useEffect(() => {
    fetchPricing()
      .then(data => setPricing(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleOverlay = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleGetPro = async () => {
    if (!user) { signIn(); return; }
    setError(null);
    setLoading(true);
    try {
      const url = await createCheckoutSession(plan);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message ?? 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  const priceLabel = p => `$${pricing[p].amount.toFixed(2)}`;
  const periodLabel = p => `${PLAN_META[p].suffix} ${pricing[p].currency}`;
  const selectedPlan = { ...PLAN_META[plan], price: priceLabel(plan), period: periodLabel(plan) };

  return createPortal(
    <div className="pro-modal-overlay" ref={overlayRef} onClick={handleOverlay}>
      <div className="pro-modal" role="dialog" aria-modal="true">

        {/* Hero */}
        <div className="pro-modal__hero">
          <button className="pro-modal__close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
          <div className="pro-modal__icon">🚀</div>
          <h2 className="pro-modal__hero-title">Motoroam Pro</h2>
          <p className="pro-modal__hero-sub">Sharper routing, weather &amp; richer rides.</p>
        </div>

        <div className="pro-modal__body">

          {/* Sign-in nudge */}
          {!user && (
            <div className="pro-modal__signin-nudge">
              <p className="pro-modal__nudge-text">Sign in first to unlock Pro features</p>
              <button className="pro-modal__google-btn" onClick={signIn}>
                <GoogleIcon />
                Continue with Google
              </button>
              <div className="pro-modal__divider"><span>then choose a plan</span></div>
            </div>
          )}

          {/* Plan comparison */}
          <div className="pro-modal__plans">
            <div className="pro-modal__plan">
              <div className="pro-modal__plan-header">
                <span className="pro-modal__plan-name">Free</span>
                <span className="pro-modal__plan-price">$0</span>
              </div>
              <ul className="pro-modal__features">
                {FREE_FEATURES.map(f => (
                  <li key={f} className="pro-modal__feature"><CheckIcon />  {f}</li>
                ))}
              </ul>
            </div>

            <div className="pro-modal__plan pro-modal__plan--pro">
              <div className="pro-modal__plan-badge">Best for riders</div>

              {/* Plan toggle */}
              <div className="pro-modal__plan-toggle">
                {Object.entries(PLAN_META).map(([key, p]) => (
                  <button
                    key={key}
                    className={`pro-modal__toggle-btn${plan === key ? ' active' : ''}`}
                    onClick={() => setPlan(key)}
                  >
                    {p.label}
                    {p.badge && <span className="pro-modal__toggle-badge">{p.badge}</span>}
                  </button>
                ))}
              </div>

              <div className="pro-modal__plan-header">
                <span className="pro-modal__plan-name">Pro</span>
                <div className="pro-modal__plan-price-block">
                  <span className="pro-modal__plan-price pro-modal__plan-price--pro">{selectedPlan.price}</span>
                  <span className="pro-modal__plan-per">{selectedPlan.period}</span>
                </div>
              </div>
              <ul className="pro-modal__features">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="pro-modal__feature pro-modal__feature--pro"><CheckIcon /> {f}</li>
                ))}
              </ul>
            </div>
          </div>

          {error && <p className="pro-modal__error">{error}</p>}

          <button
            className="pro-modal__cta"
            onClick={handleGetPro}
            disabled={loading}
          >
            {loading
              ? <span className="pro-modal__cta-spinner" />
              : `Get Motoroam Pro — ${selectedPlan.price}${selectedPlan.period}`
            }
          </button>
          <button className="pro-modal__dismiss" onClick={onClose}>Maybe later</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 6.5l3 3 6-6"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
